import { type CollectionCharacteristics } from '@prisma/client'
import { useTranslation } from 'react-i18next'
import { CONTINUITY_OPTIONS } from '#app/routes/admin+/materials+/types.ts'
import { cn } from '#app/utils/misc.tsx'
import {
	PublicDialog,
	PublicDialogContent,
	PublicDialogHeader,
	PublicDialogTitle,
	PublicDialogTrigger,
} from '../marketing/public-dialog'
import { Icon } from '../ui/icon'

import Mirror from '/img/icons/mirror.svg'
import Techonology from '/img/icons/technology.svg'
import VerticalContinuity from '/img/icons/vertical-continuity.svg'

function DialogSectionTitle({ children }: { children: React.ReactNode }) {
	return (
		<p className="mb-sm border-b border-brand-dusk font-semibold uppercase tracking-[0.2em] text-brand-dusk">
			{children}
		</p>
	)
}

export default function MoreInformationDialog({
	product,
}: {
	product: {
		name: string
		characteristics: Pick<
			CollectionCharacteristics,
			'bookmatched' | 'continuity' | 'fourthDimension'
		>
		variants: ({
			width: {
				width: number
			}
			size: {
				size_eu: string
				size_us: string
			}
			finish: {
				translations: {
					name: string
				}[]
			}
		} & {
			id: string
			createdAt: Date
			updatedAt: Date
			collectionTypologyId: string
			collectionFinishId: string
			collectionWidthId: string
			collectionSizeId: string
			sapCode: string | null
			collectionId: string
		})[]
	}
}) {
	const { t } = useTranslation('products')
	const hasMeaningfulCharacteristics =
		product.characteristics.bookmatched ||
		product.characteristics.fourthDimension ||
		(product.characteristics.continuity &&
			product.characteristics.continuity === CONTINUITY_OPTIONS.VERTICAL)

	return (
		<PublicDialog>
			<PublicDialogTrigger>
				<button>
					<Icon name="circle-plus" size="lg" />
				</button>
			</PublicDialogTrigger>
			<PublicDialogContent className="max-w-[80vw] min-[2000px]:max-w-[45vw]">
				<PublicDialogHeader>
					<PublicDialogTitle>/ {product.name} /</PublicDialogTitle>
				</PublicDialogHeader>

				<div className="mt-sm grid grid-cols-1 gap-sm md:grid-cols-3 lg:mt-md">
					<div
						className={cn(
							'col-span-2 flex flex-col gap-sm',
							!hasMeaningfulCharacteristics && 'col-span-full',
						)}
					>
						<DialogSectionTitle>{t('public.product.codes')}</DialogSectionTitle>

						<div className="flex flex-col gap-xxxs">
							{product.variants.map((variant) => (
								<div key={variant.id} className="flex gap-xxs font-light">
									<p>{variant.width.width} mm</p>
									<p>|</p>
									<p>{variant.finish.translations[0]!.name}</p>
									<p className="w-full flex-1 justify-self-end text-end font-semibold">
										{variant.sapCode}
									</p>
								</div>
							))}
						</div>
					</div>
					{hasMeaningfulCharacteristics && (
						<div className={'flex flex-col gap-sm'}>
							<DialogSectionTitle>
								{t('public.product.characteristics')}
							</DialogSectionTitle>
							<div className="flex flex-col gap-xxxs font-light">
								{product.characteristics.bookmatched && (
									<div className="flex items-center gap-xxs">
										<img src={Mirror} className="h-7 w-7" />
										<p>{t('public.product.characteristics.bookmatch')}</p>
									</div>
								)}
								{product.characteristics.fourthDimension && (
									<div className="flex items-center gap-xxs">
										<img src={Techonology} className="h-7 w-7" />
										<p>{t('public.product.characteristics.4d')}</p>
									</div>
								)}
								{product.characteristics.continuity &&
									product.characteristics.continuity ===
										CONTINUITY_OPTIONS.VERTICAL && (
										<div className="flex items-center gap-xxs">
											<img src={VerticalContinuity} className="h-7 w-7" />
											<p>
												{t(
													'public.product.characteristics.continuity.vertical',
												)}
											</p>
										</div>
									)}
							</div>
						</div>
					)}
				</div>
			</PublicDialogContent>
		</PublicDialog>
	)
}
